import { useEffect } from 'react';

import { LoginState, OAuth2Error, decodeState } from '@collab/libs/auth/oauth2';
import { popLoginCookie } from '@collab/utils/session';

import useOAuth2Params, { OAuth2Params } from './useOAuth2Params';

type Props = {
  handleExchange: (
    exchangeCode: string,
    verifier: string,
    state: LoginState,
  ) => Promise<void>;
  handleException: (error: Error) => void;
};

const useOAuth2Exchange = ({ handleExchange, handleException }: Props) => {
  const oauth2Params = useOAuth2Params();

  const handleParams = async (params: OAuth2Params) => {
    const state = params.state ? decodeState(params.state) : undefined;

    if (params.type === 'error') {
      const redirect = state?.postLoginRedirectUrl;

      throw new OAuth2Error(params.errorCode, params.description, redirect);
    }

    if (params.type === 'success') {
      const loginCookie = popLoginCookie();

      if (loginCookie) {
        if (!state) {
          throw new MissingState();
        }

        if (state.noice !== loginCookie.noice) {
          throw new NoiceMissmatch(state.postLoginRedirectUrl);
        }

        await handleExchange(params.exchangeCode, loginCookie.verifier, state);
      }
    }
  };

  useEffect(() => {
    if (oauth2Params) {
      handleParams(oauth2Params).catch((e) => handleException(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauth2Params]);
};

class MissingState extends OAuth2Error {
  constructor() {
    super('missing_state', 'Missing state param');
  }
}

class NoiceMissmatch extends OAuth2Error {
  constructor(redirectUrl?: string) {
    super(
      'noice_missmatch',
      'Noice value from loginflow did not match noice value in cookie',
      redirectUrl,
    );
  }
}

export default useOAuth2Exchange;
